import React, { useState, useEffect, useRef } from 'react';
import { 
  PencilIcon, 
  TrashIcon, 
  SearchIcon, 
  CalendarIcon, 
  PlusIcon, 
  DocumentDownloadIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  ExclamationCircleIcon, 
  BanIcon,
  PencilSquareIcon 
} from '@heroicons/react/outline';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import toastService from '../../../services/toast';
import usersService from '../../../services/users';
import FormModal from '../../../components/common/FormModal';
import AlertConfirmModal from '../../common/AlertConfirmModal';
import { ROLES, checkRoleType } from '../../../services/permissionsService';
import { usePermissions } from '../../../contexts/PermissionsContext';

const AdminsPage = () => {
  const { hasPermission } = usePermissions();
  const searchTimeoutRef = useRef(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]); // قائمة الأدوار المتاحة من السيرفر
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    status: 'نشط',
    role_id: null // استخدام role_id بدلاً من role
  });
  const [newPhoneError, setNewPhoneError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [phoneError, setPhoneError] = useState('');
  const [showBanModal, setShowBanModal] = useState(false);
  const [adminToBlock, setAdminToBlock] = useState(null);
  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);

  // جلب الأدوار من السيرفر عند تحميل الصفحة
  useEffect(() => {
    fetchAvailableRoles();
    loadAdmins(1);
  }, []);

  useEffect(() => {
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, []);

  // جلب الأدوار المتاحة من السيرفر
  const fetchAvailableRoles = async () => {
    try {
      const rolesData = await usersService.fetchRoles();
      // تم استلام الأدوار بنجاح
      if (rolesData && Array.isArray(rolesData.data)) {
        setAvailableRoles(rolesData.data);
        
        // تعيين الدور الافتراضي للمشرفين (admin أو supervisor)
        if (rolesData.data.length > 0) {
          // البحث عن دور المشرف باستخدام دالة التحقق الجديدة
          const adminRole = rolesData.data.find(role => checkRoleType(role.name) === 'ADMIN');
          const supervisorRole = rolesData.data.find(role => checkRoleType(role.name) === 'SUPERVISOR');
          
          // تعيين الدور الافتراضي حسب الأولوية: مشرف أو مدير أو أول دور متاح
          const defaultRoleId = supervisorRole?.id || adminRole?.id || rolesData.data[0].id;
          
          setNewAdmin(prev => ({
            ...prev,
            role_id: defaultRoleId
          }));
        }
      }
    } catch (error) {
      // خطأ في استلام الأدوار
      toastService.error('حدث خطأ في جلب الأدوار المتاحة');
    }
  };

  const loadAdmins = async (page, search = null, from = null, to = null) => {
    try {
      setLoading(true);
      const params = { page, per_page: 20 };
      if (search) params.search = search;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const response = await usersService.fetchAdmins(params);
      setAdmins(response.data);
      setLastPage(response.last_page);
      setCurrentPage(response.current_page);
    } catch (error) {
      toastService.error(error.message || 'حدث خطأ في تحميل قائمة المشرفين');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) { setSearching(false); loadAdmins(1); return; }
    setSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      loadAdmins(1, value.trim(), dateFrom, dateTo);
      setSearching(false);
    }, 300);
  };

  const handleDateFromChange = (e) => {
    const value = e.target.value;
    setDateFrom(value);
    setCurrentPage(1);
    loadAdmins(1, searchQuery, value, dateTo);
  };

  const handleDateToChange = (e) => {
    const value = e.target.value;
    setDateTo(value);
    setCurrentPage(1);
    loadAdmins(1, searchQuery, dateFrom, value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadAdmins(page, searchQuery, dateFrom, dateTo);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) handlePageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < lastPage) handlePageChange(currentPage + 1);
  };

  const handleExportToExcel = () => {
    // تحويل البيانات إلى تنسيق مناسب للإكسل
    const dataToExport = admins.map(admin => ({
      'اسم المشرف': admin.name,
      'البريد الإلكتروني': admin.email,
      'رقم الجوال': admin.phone,
      'تاريخ التسجيل': admin.createdAt ? admin.createdAt.split('-').map((part, index) => 
        index === 2 ? part : part.padStart(2, '0')).join('-') : '',
      'الحالة': admin.status
    }));

    // إنشاء ورقة عمل جديدة
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "المشرفين");

    // تحميل الملف
    XLSX.writeFile(wb, "admins-report.xlsx");
  };

  const validateForm = (admin, isEdit = false) => {
    const errors = {};
    
    if (!admin?.name || admin.name.trim() === '') {
      errors.name = 'الاسم مطلوب';
    }

    if (!admin?.email || admin.email.trim() === '') {
      errors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/\S+@\S+\.\S+/.test(admin.email)) {
      errors.email = 'البريد الإلكتروني غير صالح';
    }

    if (!admin?.phone || admin.phone.trim() === '') {
      errors.phone = 'رقم الجوال مطلوب';
    }

    if (!isEdit && (!admin?.password || admin.password.trim() === '')) {
      errors.password = 'كلمة المرور مطلوبة';
    }
    
    if (!admin?.role_id) {
      errors.role_id = 'الدور مطلوب';
    }

    return errors;
  };

  const handleAddAdmin = async () => {
    try {
      const errors = validateForm(newAdmin);
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        toastService.error(
          <div className="text-right" dir="rtl">
            <p className="font-bold mb-2">يرجى تصحيح الأخطاء التالية:</p>
            <ul className="list-disc mr-5">
              {Object.values(errors).map((error, index) => (
                <li key={index} className="text-right">{error}</li>
              ))}
            </ul>
          </div>
        );
        return;
      }

      const response = await usersService.createAdmin(newAdmin);
      toastService.success('تم إضافة المستخدم بنجاح');
      setShowAddModal(false);
      
      // تحديث داخلي للقائمة بدلاً من إعادة التحميل الكامل
      if (response.data && response.data.user) {
        // إضافة المستخدم الجديد إلى القائمة
        setAdmins(prevAdmins => [response.data.user, ...prevAdmins]);
      } else {
        // في حالة عدم استجابة المستخدم بالشكل المتوقع، نقوم بإعادة التحميل
        loadAdmins();
      }
      
      setNewAdmin({
        name: '',
        email: '',
        phone: '',
        password: '',
        role_id: '',
        status: 'نشط'
      });
      setFormErrors({});
    } catch (error) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">حدث خطأ:</p>
          <ul className="list-disc mr-5">
            {error.split('\n').map((err, index) => (
              <li key={index} className="text-right">{err}</li>
            ))}
          </ul>
        </div>
      );
    }
  };

  const handleEditAdmin = async () => {
    try {
      const errors = validateForm(selectedAdmin, true);
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      const { id, password, ...adminData } = selectedAdmin;
      
      // تأكد من أن role_id موجود وصالح
      if (adminData.role_id) {
        // تحويل role_id إلى رقم للتأكد من أنه ليس نص
        adminData.role_id = parseInt(adminData.role_id);
        // إضافة حقل role للتوافق مع الباك إند
        const selectedRole = availableRoles.find(role => role.id === adminData.role_id);
        if (selectedRole) {
          adminData.role = selectedRole.name;
        }
      }
      
      // 1. تحديث بيانات المشرف الأساسية (بدون كلمة المرور)
      const response = await usersService.updateAdmin(id, adminData);
      
      // 2. لو فيه كلمة مرور جديدة، استدعي API المخصصة لتغيير الباسورد للمشرفين
      if (password && password.trim() !== '') {
        // Change admin password
        await usersService.changeAdminPassword(id, { password });
        // Password changed successfully
      }
      
      toastService.success('تم تحديث بيانات المشرف بنجاح');
      setShowEditModal(false);
      
      // التحقق من تغيير الدور
      const newRole = adminData.role; // الدور الجديد للمستخدم
      
      // تحقق مما إذا كان الدور قد تغير من مشرف إلى دور آخر
      // إذا كان الدور ليس مدير ولا مشرف، فهذا يعني أنه تم نقله لدور آخر
      const isRoleChanged = newRole && (newRole !== ROLES.ADMIN && newRole !== ROLES.SUPERVISOR);
      
      if (isRoleChanged) {
        // إذا تغير الدور من مشرف إلى دور آخر، نحذف المستخدم من القائمة
        setAdmins(prevAdmins => prevAdmins.filter(admin => admin.id !== id));
      } else {
        // إذا لم يتغير الدور أو تغير لدور مشرف آخر، نحدث البيانات فقط
        // تحديث المستخدم في القائمة
        if (response.data && response.data.user) {
          setAdmins(prevAdmins => 
            prevAdmins.map(admin => 
              admin.id === id ? response.data.user : admin
            )
          );
        } else {
          setAdmins(prevAdmins => 
            prevAdmins.map(admin => 
              admin.id === id ? { ...admin, ...adminData } : admin
            )
          );
        }
      }
      
      setFormErrors({});
    } catch (error) {
      toastService.error(error.message || 'حدث خطأ أثناء تحديث بيانات المشرف');
    }
  };

  const handleDeleteAdmin = async () => {
    try {
      const idToDelete = selectedAdmin.id;
      await usersService.deleteAdmin(idToDelete);
      toastService.success('تم حذف المشرف بنجاح');
      setShowDeleteModal(false);
      setSelectedAdmin(null);
      
      // تحديث داخلي للقائمة بحذف المشرف منها
      setAdmins(prevAdmins => 
        prevAdmins.filter(admin => admin.id !== idToDelete)
      );
    } catch (error) {
      toastService.error(error.message || 'حدث خطأ أثناء حذف المشرف');
    }
  };

  const handleChangeStatus = async () => {
    try {
      const idToUpdate = adminToBlock.id;
      const newStatus = adminToBlock.status === 'محظور' ? 'نشط' : 'محظور';
      const response = await usersService.changeAdminStatus(idToUpdate, newStatus);
      toastService.success(adminToBlock.status === 'محظور' ? 'تم فك حظر المشرف بنجاح' : 'تم حظر المشرف بنجاح');
      setShowBanModal(false);
      
      // تحديث داخلي للقائمة بتغيير حالة المشرف
      if (response.data && response.data.user) {
        // تحديث المستخدم في القائمة
        setAdmins(prevAdmins => 
          prevAdmins.map(admin => 
            admin.id === idToUpdate ? response.data.user : admin
          )
        );
      } else {
        // تحديث الحالة بناءً على المعلومات المتوفرة
        setAdmins(prevAdmins => 
          prevAdmins.map(admin => 
            admin.id === idToUpdate ? {...admin, status: newStatus} : admin
          )
        );
      }
      
      setAdminToBlock(null);
    } catch (error) {
      toastService.error(error.message || 'حدث خطأ أثناء تغيير حالة المشرف');
    }
  };

  const handleDeleteClick = (admin) => {
    setSelectedAdmin(admin);
    setShowDeleteModal(true);
  };

  const handleEditClick = (admin) => {
    // نسخة من المستخدم لكن بدون دور محدد
    const adminWithEmptyRole = {
      ...admin,
      role_id: '', // تفريغ الدور
      role: '',    // تفريغ الدور
    };
    
    setSelectedAdmin(adminWithEmptyRole);
    setShowEditModal(true);
  };

  const handleBanClick = (admin) => {
    setAdminToBlock(admin);
    setShowBanModal(true);
  };

  return (
    <div className="p-6">
      {/* Search and Filter Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
        {/* Search Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">قائمة المشرفين</h2>
          <div className="flex gap-4">
            {hasPermission && hasPermission('create_admin') && (
              <div className="relative group">
                <PlusIcon 
                  className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors duration-200"
                  onClick={() => setShowAddModal(true)}
                />
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                  إضافة مشرف جديد
                </div>
              </div>
            )}
            
            {hasPermission && hasPermission('view_admins') && (
              <div className="relative group">
                <DocumentDownloadIcon 
                  className="h-6 w-6 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 cursor-pointer transition-colors duration-200"
                  onClick={handleExportToExcel}
                />
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                  استخراج في ملف إكسل
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Search Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Search Input */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">البحث عن مشرف</label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    {searching ? (
                      <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <SearchIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="البحث عن مشرف..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="input-base pr-10"
                  />
                </div>
            </div>

            {/* Date From */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">من تاريخ</label>
              <div className="relative rounded-lg">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={handleDateFromChange}
                  className="input-base pl-10 pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10"
                  ref={(input) => {
                    if (input) {
                      dateFromRef.current = input;
                    }
                  }}
                />
                <div 
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  onClick={() => dateFromRef.current?.showPicker()}
                >
                  <CalendarIcon className="h-5 w-5 text-green-500 dark:text-green-400" />
                </div>
              </div>
            </div>

            {/* Date To */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">إلى تاريخ</label>
              <div className="relative rounded-lg">
                <input
                  type="date"
                  value={dateTo}
                  onChange={handleDateToChange}
                  className="input-base pl-10 pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10"
                  ref={(input) => {
                    if (input) {
                      dateToRef.current = input;
                    }
                  }}
                />
                <div 
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  onClick={() => dateToRef.current?.showPicker()}
                >
                  <CalendarIcon className="h-5 w-5 text-orange-500 dark:text-orange-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">إجمالي المشرفين</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{admins.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">المشرفين الغير نشطين</div>
          <div className="mt-1 text-2xl font-semibold text-yellow-600 dark:text-yellow-400">
            {admins.filter(a => a.status === 'غير نشط').length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">المشرفين المحظورين</div>
          <div className="mt-1 text-2xl font-semibold text-red-600 dark:text-red-400">
            {admins.filter(a => a.status === 'محظور').length}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className={`${admins.length > 0 ? 'overflow-x-auto' : ''}`}>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-center" style={{ tableLayout: 'fixed' }}>
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="w-40 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  اسم المشرف
                </th>
                <th scope="col" className="w-44 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  البريد الإلكتروني
                </th>
                <th scope="col" className="w-40 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  رقم الجوال
                </th>
                <th scope="col" className="w-40 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  تاريخ التسجيل
                </th>
                <th scope="col" className="w-32 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  الحالة
                </th>
                <th scope="col" className="w-24 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={`skeleton-${i}`} className="animate-pulse">
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="flex gap-3 justify-center">
                        <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded-full" />
                        <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded-full" />
                        <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded-full" />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                admins.map((admin) => (
                  <tr key={admin.id}>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{admin.name}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{admin.email}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100" dir="ltr">
                        {admin.phone}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100" dir="ltr">
                        {admin.createdAt.split('-').map((part, index) => 
                          index === 2 ? part : part.padStart(2, '0')).join('-')}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                        admin.status === 'نشط' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : admin.status === 'غير نشط'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {admin.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="flex gap-3 justify-center">
                        {hasPermission && hasPermission('edit_admin') && (
                          <div className="relative group">
                            <BanIcon 
                              className={`h-5 w-5 cursor-pointer ${
                                admin.status === 'محظور'
                                  ? 'text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400'
                                  : 'text-yellow-600 hover:text-yellow-700 dark:text-yellow-500 dark:hover:text-yellow-400'
                              }`}
                              onClick={() => handleBanClick(admin)}
                            />
                            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50">
                              {admin.status === 'محظور' ? 'فك حظر المشرف' : 'حظر المشرف'}
                            </div>
                          </div>
                        )}
                        {hasPermission && hasPermission('edit_admin') && (
                          <PencilIcon 
                            className="h-5 w-5 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer" 
                            onClick={() => handleEditClick(admin)}
                          />
                        )}
                        {hasPermission && hasPermission('delete_admin') && (
                          <TrashIcon 
                            className="h-5 w-5 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 cursor-pointer" 
                            onClick={() => handleDeleteClick(admin)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <tr>
                <td colSpan="7">
                  <div className="flex justify-between items-center mt-2 mb-2">
                    <div className="flex justify-center items-center mx-auto">
                      <button 
                        className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                      >
                        <ChevronRightIcon className="h-6 w-6" />
                      </button>
                      <span className="mx-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {currentPage} / {lastPage}
                      </span>
                      <button 
                        className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
                        onClick={handleNextPage}
                        disabled={currentPage === lastPage}
                      >
                        <ChevronLeftIcon className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      {/* نافذة منبثقة لإضافة مشرف جديد */}
      <FormModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setNewAdmin({
            name: '',
            email: '',
            phone: '',
            password: '',
            status: 'نشط',
            role: availableRoles.length > 0 ? availableRoles[0].name : ROLES.ADMIN // استخدم الدور الأول من السيرفر أو القيمة الافتراضية
          });
          setFormErrors({});
        }}
        onSubmit={handleAddAdmin}
        title="إضافة مشرف جديد"
        submitText="إضافة"
        cancelText="إلغاء"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">الاسم <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={newAdmin.name}
            onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
            className="input-base"
          />
          {formErrors.name && (
            <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">البريد الإلكتروني <span className="text-red-500">*</span></label>
          <input
            type="email"
            value={newAdmin.email}
            onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
            className="input-base"
            dir="ltr"
          />
          {formErrors.email && (
            <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">رقم الجوال <span className="text-red-500">*</span></label>
          <input
            type="tel"
            maxLength="10"
            value={newAdmin.phone}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value === '') {
                setNewAdmin({...newAdmin, phone: value});
                setNewPhoneError('');
              } else if (value.length <= 2) {
                setNewAdmin({...newAdmin, phone: value});
                if (value.length === 2 && value !== '05') {
                  setNewPhoneError('رقم الجوال يجب أن يبدأ بـ 05');
                } else {
                  setNewPhoneError('');
                }
              } else if (value.startsWith('05')) {
                setNewAdmin({...newAdmin, phone: value});
                setNewPhoneError('');
              }
            }}
            className={`input-base ${newPhoneError ? 'border-red-500' : ''}`}
            placeholder="05xxxxxxxx"
            dir="ltr"
          />
          {newPhoneError && (
            <p className="mt-1 text-sm text-red-500">{newPhoneError}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">الدور <span className="text-red-500">*</span></label>
          <select
            value={newAdmin.role_id || ''}
            onChange={(e) => setNewAdmin({...newAdmin, role_id: parseInt(e.target.value)})}
            className="input-base"
          >
            {availableRoles.length > 0 ? (
              // عرض الأدوار من السيرفر
              // استخدام معرف الدور (id) كقيمة والعربي (display_name) للعرض
              availableRoles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.display_name || role.name}
                </option>
              ))
            ) : (
              // في حالة عدم وجود أدوار من السيرفر، استخدم القيم الافتراضية
              <>
                <option value="admin">مدير النظام</option>
                <option value="supervisor">مشرف</option>
                <option value="instructor">مدرب</option>
                <option value="trainee">متدرب</option>
              </>
            )}
          </select>
          {formErrors.role_id && (
            <p className="mt-1 text-sm text-red-500">{formErrors.role_id}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">كلمة المرور <span className="text-red-500">*</span></label>
          <input
            type="password"
            value={newAdmin.password}
            onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
            className="input-base"
          />
          {formErrors.password && (
            <p className="mt-1 text-sm text-red-500">{formErrors.password}</p>
          )}
        </div>
      </FormModal>
      {/* مودال التعديل */}
      <FormModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedAdmin(null);
          setFormErrors({});
        }}
        onSubmit={handleEditAdmin}
        title="تعديل بيانات المشرف"
        submitText="حفظ"
        cancelText="إلغاء"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">الاسم <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={selectedAdmin?.name || ''}
            onChange={(e) => setSelectedAdmin({...selectedAdmin, name: e.target.value})}
            className="input-base"
          />
          {formErrors.name && (
            <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">البريد الإلكتروني <span className="text-red-500">*</span></label>
          <input
            type="email"
            value={selectedAdmin?.email || ''}
            onChange={(e) => setSelectedAdmin({...selectedAdmin, email: e.target.value})}
            className="input-base"
            dir="ltr"
          />
          {formErrors.email && (
            <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">رقم الجوال <span className="text-red-500">*</span></label>
          <input
            type="tel"
            maxLength="10"
            value={selectedAdmin?.phone || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value === '') {
                setSelectedAdmin({...selectedAdmin, phone: value});
                setPhoneError('');
              } else if (value.length <= 2) {
                setSelectedAdmin({...selectedAdmin, phone: value});
                if (value.length === 2 && value !== '05') {
                  setPhoneError('رقم الجوال يجب أن يبدأ بـ 05');
                } else {
                  setPhoneError('');
                }
              } else if (value.startsWith('05')) {
                setSelectedAdmin({...selectedAdmin, phone: value});
                setPhoneError('');
              }
            }}
            className={`input-base ${phoneError ? 'border-red-500' : ''}`}
            placeholder="05xxxxxxxx"
            dir="ltr"
          />
          {phoneError && (
            <p className="mt-1 text-sm text-red-500">{phoneError}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">الدور <span className="text-red-500">*</span></label>
          <select
            value={selectedAdmin?.role_id || ''}
            onChange={(e) => {
              // التحقق من أن القيمة ليست فارغة قبل التحويل إلى رقم
              const roleId = e.target.value ? parseInt(e.target.value) : '';
              setSelectedAdmin({...selectedAdmin, role_id: roleId})
            }}
            className="input-base"
          >
            <option value="">اختر الدور المناسب</option>
            {availableRoles.length > 0 ? (
              availableRoles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.display_name || role.name}
                </option>
              ))
            ) : (
              <>
                <option value="1">مدير النظام</option>
                <option value="2">مشرف</option>
                <option value="3">مدرب</option>
                <option value="4">متدرب</option>
              </>
            )}
          </select>
          {formErrors.role_id && (
            <p className="mt-1 text-sm text-red-500">{formErrors.role_id}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">كلمة المرور (اختياري)</label>
          <input
            type="password"
            value={selectedAdmin?.password || ''}
            onChange={(e) => setSelectedAdmin({...selectedAdmin, password: e.target.value})}
            placeholder="اتركها فارغة للاحتفاظ بكلمة المرور الحالية"
            className="input-base"
          />
        </div>
      </FormModal>
      {/* نافذة تأكيد الحذف */}
      {showDeleteModal && selectedAdmin && (
        <AlertConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedAdmin(null);
          }}
          onConfirm={handleDeleteAdmin}
          icon={ExclamationCircleIcon}
          iconColorClass="text-red-500"
          title="تأكيد حذف المشرف"
          message="هل أنت متأكد من حذف المشرف؟"
          details={selectedAdmin.name}
          note="سيتم حذف المشرف نهائياً من النظام"
          confirmText="تأكيد الحذف"
          cancelText="إلغاء"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
          noteClass="text-red-600 dark:text-red-400"
        />
      )}
      {/* نافذة تأكيد الحظر */}
      {showBanModal && adminToBlock && (
        <AlertConfirmModal
          isOpen={showBanModal}
          onClose={() => {
            setShowBanModal(false);
            setAdminToBlock(null);
          }}
          onConfirm={handleChangeStatus}
          icon={BanIcon}
          iconColorClass={adminToBlock.status === 'محظور' ? 'text-green-500' : 'text-yellow-500'}
          title={adminToBlock.status === 'محظور' ? 'تأكيد فك حظر المشرف' : 'تأكيد حظر المشرف'}
          message={adminToBlock.status === 'محظور' ? 'هل أنت متأكد من فك حظر المشرف؟' : 'هل أنت متأكد من حظر المشرف؟'}
          details={adminToBlock.name}
          confirmButtonClass={adminToBlock.status === 'محظور' ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'}
        />
      )}
    </div>
  );
};

export default AdminsPage;
